import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Box, Typography } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { feesSectionTableHeaderData } from "./feesSectionTableHeaderData";
import { GetLookupItemsByLookupTypeName } from "../../../../../../../../Services";
import {
  BasicTable,
  CustomSwitch,
  CustomTableHeader,
  ReskinningInputs,
  TableEditDeleteActions,
  CustomButton,
  CustomAutocomplete,
} from "../../../../../../../../Components";

// icons
import { PlusIcon } from "../../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

const initialFeeItem = () => ({
  feeTypeId: null,
  feeType: "",
  feeAmount: "",
  isFixed: false,
  id: crypto.randomUUID(),
});

const feeSchema = Yup.object().shape({
  feeTypeId: Yup.number()
    .typeError("Fee type is required")
    .required("Fee type is required"),
  feeAmount: Yup.number()
    .typeError("Amount is required")
    .required("Amount is required"),
  isFixed: Yup.boolean(),
});

function FeesSection() {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewPropertiesView");
  const { values, setFieldValue, touched, errors } = useFormikContext();
  const { isDarkMode } = useSelector((state) => state.theme);

  const [newFee, setNewFee] = useState(initialFeeItem());
  const [feeTypes, setFeeTypes] = useState([]);
  const [feeTypesLoading, setFeeTypesLoading] = useState(false);
  const [activeItem, setActiveItem] = useState({});
  const [localErrors, setLocalErrors] = useState({});

  const handleNewChange = (field, value) => {
    setNewFee((prev) => ({ ...prev, [field]: value }));
    setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleAdd = async (push) => {
    try {
      await feeSchema.validate(newFee, { abortEarly: false });
      push({ ...newFee, id: crypto.randomUUID() });
      setNewFee(initialFeeItem());
      setLocalErrors({});
    } catch (err) {
      if (err.inner) {
        const errorsObj = err.inner.reduce((acc, curr) => {
          acc[curr.path] = curr.message;
          return acc;
        }, {});
        setLocalErrors(errorsObj);
      }
    }
  };

  useEffect(() => {
    setFeeTypesLoading(true);
    GetLookupItemsByLookupTypeName({
      lookUpName: "FeeType",
      pageIndex: 1,
      pageSize: 100,
    })
      .then((res) => setFeeTypes(res.result || []))
      .finally(() => setFeeTypesLoading(false));
  }, []);
  return (
    <>
      <FieldArray name="paymentPlanFees">
        {({ push, remove }) => (
          <>
            <Box className={styles.containerInputSection}>
              <Box style={{ display: "flex", gap: "16px 32px" }}>
                <Typography className={styles.titleFiled}>
                  {translate("additional-fees")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    maxWidth: "416px",
                  }}
                >
                  <CustomAutocomplete
                    containerClasses={styles.autocompleteWidthOverride}
                    rootCustomClasses={styles.autocompleteWidthOverride}
                    inputPlaceholder={translate("fee-type")}
                    idRef="activitiesSearchRef"
                    options={feeTypes}
                    isLoading={feeTypesLoading}
                    getOptionLabel={(option) => option?.lookupItemName ?? ""}
                    renderOption={(option) => option?.lookupItemName ?? ""}
                    multiple={false}
                    selectedValues={
                      feeTypes.find(
                        (f) => f.lookupItemId === newFee.feeTypeId
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      handleNewChange(
                        "feeTypeId",
                        newValue?.lookupItemId ?? null
                      );
                      handleNewChange(
                        "feeType",
                        newValue?.lookupItemName ?? ""
                      );
                    }}
                    hasError={!!localErrors.feeTypeId}
                    errorMessage={localErrors.feeTypeId}
                    submitted={localErrors.feeTypeId}
                    withoutSearchButton
                  />
                  <CustomSwitch
                    size={"md"}
                    label={translate("by-percentage")}
                    checked={!newFee.isFixed}
                    onChange={() => handleNewChange("isFixed", !newFee.isFixed)}
                    isDisabled={false}
                  />
                  <ReskinningInputs
                    startAdornment={
                      !newFee.isFixed && (
                        <span className={styles.adornment}>%</span>
                      )
                    }
                    inputPlaceholder={translate("amount")}
                    style={{ maxWidth: "100%" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={newFee.feeAmount}
                    onInputChanged={(e) =>
                      handleNewChange("feeAmount", e.target.value)
                    }
                    error={!!localErrors.feeAmount}
                    helperText={localErrors.feeAmount}
                  />
                </Box>
              </Box>
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography style={{ width: "35%" }}></Typography>
              <Box display="flex" justifyContent="flex-start" width="100%">
                <CustomButton
                  boxShadow="none"
                  variant="text"
                  color="secondary"
                  style={{ color: palette.button.tertiaryGray_fg }}
                  onClick={() => handleAdd(push)}
                >
                  <PlusIcon
                    width="20"
                    height="20"
                    fill={palette.button.tertiaryGray_fg}
                  />
                  {translate("add-another")}
                </CustomButton>
              </Box>
            </Box>
            <Box className={styles.containerInputSection}>
              <BasicTable
                rowHeight={84}
                setActiveItem={setActiveItem}
                setActiveItemOnRowClick={(item) => {
                  setActiveItem(item);
                }}
                rowsData={values.paymentPlanFees || []}
                columns={feesSectionTableHeaderData(isDarkMode)?.map(
                  (column) => ({
                    ...column,
                    renderHeader: (params) => (
                      <CustomTableHeader
                        params={params}
                        title={column.headerName}
                        isHiddenFilter={true}
                      />
                    ),
                  })
                )}
                tableActions={({ anchorPosition }) => {
                  if (!activeItem || Object.keys(activeItem).length === 0)
                    return null;
                  const activeIndex = values.installment.findIndex(
                    (item) => item.id === activeItem.id
                  );

                  if (activeIndex === -1) return null;
                  return (
                    <TableEditDeleteActions
                      // anchorPosition={anchorPosition}
                      // onDelete={() => {
                      //   remove(activeIndex);
                      //   setActiveItem({});
                      //   setAnchorPosition({ x: 0, y: 0 });
                      // }}
                      anchorPosition={{ y: 200 }}
                      onDelete={() => alert("Delete")}
                    />
                  );
                }}
                emptyState={{ NoRowsOverlay: () => <Box></Box> }}
              />
            </Box>
          </>
        )}
      </FieldArray>
    </>
  );
}

export default FeesSection;
